import { firstValueFrom } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ResourcesService } from '@mitsubishi/core';
import { PerformanceAccessory, PerformanceAccessoryAnswer } from '@mitsubishi/shared/data-access';
import { BaseUiModule } from '@mitsubishi/shared/ui';

export type PerformanceAccessoriesAssignmentDialogProps = {
  models: {
    modelName: string;
    unitUUID: string;
    performanceId?: string;
    workingCondition?: string;
    performanceAccessories: PerformanceAccessory[];
  }[];

  callBackFunction: (
    models: PerformanceAccessoriesAssignmentDialogResult['models'],
    action: PerformanceAccessoriesAssignmentDialogAction
  ) => void;
  allowClone: boolean;
};

export enum PerformanceAccessoriesAssignmentDialogAction {
  Apply,
  ApplyAndClone,
}
export type PerformanceAccessoriesAssignmentDialogResult = {
  action: PerformanceAccessoriesAssignmentDialogAction;
  models: Record<string, { performanceId?: string; accessories: { questionCode: string; answerCode: string }[] }>;
};

@Component({
  selector: 'apm-performance-accessories-dialog',
  imports: [CommonModule, BaseUiModule, FormsModule],
  templateUrl: './performance-accessories-assignment-dialog.component.html',
  styleUrl: './performance-accessories-assignment-dialog.component.scss',
})
export class PerformanceAccessoriesAssignmentDialogComponent implements OnInit {
  #resourcesService = inject(ResourcesService);

  readonly accessoriesTranslations = signal({});

  readonly groupedModels = computed(() => {
    return (
      this.data?.models.reduce<Record<string, Record<string, { key: string; value: string }[]>>>((acc, model) => {
        const groupAccessories = (performanceAccessories: PerformanceAccessory[]) => {
          return (
            performanceAccessories.reduce<Record<string, { key: string; value: string }[]>>((acc, accessory) => {
              const answer = {
                key: accessory.answerCode,
                value: `${accessory.answerCode} - ${this.#resourcesService.getTextResource(this.accessoriesTranslations()[accessory.answerCode])}`,
              };

              if (acc[accessory.questionCode]) {
                acc[accessory.questionCode].push(answer);
              } else {
                acc[accessory.questionCode] = [answer];
              }

              return acc;
            }, {}) ?? {}
          );
        };

        acc[model.unitUUID] = groupAccessories(model.performanceAccessories);

        return acc;
      }, {}) ?? {}
    );
  });

  readonly selectedAnswers = signal<Record<string, Record<string, string>>>({});

  selectedAnswerChanged(unitUUID: string, questionCode: string, answerCode: string) {
    this.selectedAnswers.update(current => {
      const newSelectedAnswers = { ...current };
      newSelectedAnswers[unitUUID] = { ...newSelectedAnswers[unitUUID], [questionCode]: answerCode };
      return newSelectedAnswers;
    });
  }

  constructor(
    public dialogRef: MatDialogRef<PerformanceAccessoriesAssignmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: PerformanceAccessoriesAssignmentDialogProps
  ) {
    this.getAccessoriesTranslations().then(translations => {
      this.accessoriesTranslations.set(translations);
    });
  }

  ngOnInit(): void {
    const selected = Object.entries(this.groupedModels()).reduce<Record<string, Record<string, string>>>(
      (acc, [key, value]) => {
        acc[key] = Object.entries(value).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key] = value[0].key;
          return acc;
        }, {});
        return acc;
      },
      {}
    );

    this.selectedAnswers.set(selected);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  apply(clone = false) {
    const models = Object.entries(this.selectedAnswers()).reduce<
      Record<string, { performanceId?: string; accessories: PerformanceAccessoryAnswer[] }>
    >((acc, [unitUUID, answers]) => {
      const model = this.data?.models.find(m => m.unitUUID === unitUUID);
      acc[unitUUID] = {
        performanceId: model?.performanceId,
        accessories: Object.entries(answers).map(([questionCode, answerCode]) => ({ questionCode, answerCode })),
      };
      return acc;
    }, {});

    this.data?.callBackFunction(
      models,
      clone
        ? PerformanceAccessoriesAssignmentDialogAction.ApplyAndClone
        : PerformanceAccessoriesAssignmentDialogAction.Apply
    );

    this.closeDialog();
  }

  async getAccessoriesTranslations() {
    const questionCodes =
      this.data?.models
        .map(x => x.performanceAccessories)
        .flat()
        .map(accessory => accessory.questionCode) ?? [];
    const answerCodes =
      this.data?.models
        .map(x => x.performanceAccessories)
        .flat()
        .map(accessory => accessory.answerCode) ?? [];

    const translations = await firstValueFrom(
      this.#resourcesService.getAccessoryTranslations([...questionCodes, ...answerCodes])
    );

    return translations.reduce<Record<string, number>>((acc, translation) => {
      acc[translation.code] = translation.descriptionId;

      return acc;
    }, {});
  }
}
